import { useMemo, useState } from 'react';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { InstantTime } from '../../lib/time-format';
import { useZoneReadReport } from '../../components/ZoneContext';
import { HOME_ZONE } from '../../lib/zones';
import { ZoneCell, ZoneFilter } from '../../components/ZoneIndicators';
import { ActionIcon, ActionsRow } from '../../components/ActionIcon';
import { matchesAlertZone, zoneLegend, legendTotal, isPlatformWide, PLATFORM_ROW } from '../../lib/alerts-zones';
import {
  actOnAlert,
  bulkActOnAlerts,
  boardCounts,
  type Alert,
  type AlertSeverity,
  type AlertSource,
} from '../../lib/alerts-api';
import { useAlertsBoard } from '../../lib/alerts-board';

/**
 * /admin/alerts — ADR-071 Nexa-Platform alert detail view (A3).
 *
 * Built to the signed-off `ADR-071-ALERTS-DETAIL-MOCKUP.html`: severity summary
 * cards, "Show:" severity pills, the catalogue filter/search/sort controls and
 * row-select bulk actions (A4 — Acknowledge / Clear / Dismiss; bulk Clear on a
 * still-active alert confirms first), and per-row drill-in to the underlying
 * events. Grouped, critical-first (D2). The list-controls are staff-owned (A5),
 * not imported from @portal-shared.
 *
 * Role model: super_admin acts; nexa_admin views (the action buttons are hidden
 * for non-super_admin; server gates regardless).
 *
 * ZONES (Jeff's rule, 9 October 2026, verbatim): "Alerts need to be holistic and zone agnostic
 * i.e. alerts from both zones need to be presented by default, filterable by zone, but always
 * showing a summary legend for each zone." So: every zone's alerts by default (the fan-out in
 * lib/alerts-api.ts); a zone filter that narrows, never targets; a per-zone legend computed over
 * the UNFILTERED list and always visible; and a PLATFORM-WIDE alert (`scope: 'platform'`, O1
 * fix 3) labelled "All zones", shown once under every filter and counted once, in its own
 * legend row. The pure rules live in lib/alerts-zones.ts.
 *
 * ONE SHARED, REFRESHING BOARD (quality register Q1–Q3, 30 October 2026): this page, the navbar
 * indicator and the Home widget read the same alerts state (lib/alerts-board.tsx), refreshed every
 * 60 s, on returning to the tab and after every action; the page says when it was last updated.
 * The Critical card, the Critical pill and the navbar are one number (`boardCounts`), and the
 * legend has a Total row whose Critical equals them. An alert that cleared itself stays listed as
 * "Auto-cleared" until acknowledged (ADR-071 AM3, Jeff 30 October 2026).
 *
 * THE TABLE (Q18 — Jeff, 31 October 2026; built to `Docs/Mocks/alerts-board/alerts-table-mock.html` v1): the Alert
 * column takes the width ("far too narrow… it needs to be 4x that width"); Zone is AU / US / All with the full name on
 * hover ("a massive waste of space" as region names); first and last seen are one Last seen cell; Status is a short pill
 * with the auto-cleared time beneath it; and the row actions are the console's icon buttons in a labelled Actions
 * column (shared `ActionIcon`) — never text buttons. Nothing scrolls sideways at 1280 px or wider (Jeff, 14:21): every
 * column but Alert is fixed, and Alert takes what is left.
 */

type ShowFilter = 'all' | 'critical' | 'warning' | 'info' | 'acknowledged' | 'autoCleared';

const SHOW_LABEL: Record<ShowFilter, string> = {
  all: 'All',
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
  acknowledged: 'Acknowledged',
  autoCleared: 'Auto-cleared',
};
type SortKey = 'severity' | 'count' | 'last-seen';

const SOURCE_LABEL: Record<AlertSource, string> = {
  scoring: 'scoring',
  vendor: 'vendor',
  deploy: 'deploy',
  auth: 'auth',
  snippet: 'snippet',
  email: 'email',
  security: 'security',
  signup: 'signup',
  liveness: 'liveness',
  billing: 'billing',
};

const SEV_RANK: Record<AlertSeverity, number> = { critical: 3, warning: 2, info: 1 };

export function AlertsPage() {
  const meState = useStaffMe();
  const role = meState.status === 'loaded' ? meState.me.role : undefined;
  // UAT fix: alerts are operational monitoring — alerts:read AND alerts:manage are
  // {nexa_admin, nexa_support, super_admin}. nexa_support reads + manages (ack/clear); the old
  // super-only act gate was a latent misalignment the cutover preserved (design-intent audit).
  const canView = role === 'super_admin' || role === 'nexa_admin' || role === 'support';
  const canAct = role === 'super_admin' || role === 'nexa_admin' || role === 'support';
  const toast = useToast();

  const board = useAlertsBoard();
  const alerts = board.status === 'loaded' ? board.alerts : null;
  const failedZones = board.failedZones;
  const [show, setShow] = useState<ShowFilter>('all');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | AlertSource>('all');
  const [sort, setSort] = useState<SortKey>('severity');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // Jeff's rule — all zones by default; the filter narrows what is SHOWN, never what is targeted.
  const [zoneFilter, setZoneFilter] = useState('all');
  // ADR-123 Decision 1 — the navigation bar names the zones on this board and their counts.
  // The bar counts a platform-wide alert ONCE as "All zones" with the board's own flag, so it
  // never reads as an Australian alert (Jeff, 11 October 2026).
  useZoneReadReport({ rows: alerts, failedZones, filter: zoneFilter, isPlatformWide: (r) => isPlatformWide(r as Parameters<typeof isPlatformWide>[0]) });

  // ADR-120 — a zone that did not answer contributes no rows, and a shorter board is
  // indistinguishable from a quiet one. Say so rather than letting silence pass for calm.
  const zoneWarning = failedZones.length
    ? `${failedZones.join(' and ')} did not respond — alerts from ${failedZones.length === 1 ? 'that zone are' : 'those zones are'} missing from this list.`
    : null;
  // After an action, everything — this page, the navbar, the Home widget — re-reads together.
  const reload = () => void board.refresh();

  // The per-zone legend: over the UNFILTERED list, so it reads the same whatever the filter shows.
  const legend = useMemo(() => zoneLegend(alerts ?? []), [alerts]);
  const legendSum = useMemo(() => legendTotal(legend), [legend]);
  // Everything below the legend is zone-filtered; a platform-wide alert survives every filter.
  const zoneRows = useMemo(() => (alerts ?? []).filter((a) => matchesAlertZone(a, zoneFilter)), [alerts, zoneFilter]);

  // The pills follow the zone filter; the cards and the navbar are the whole board. Both come from
  // `boardCounts`, so with "All zones" selected the Critical pill IS the Critical card IS the navbar.
  const counts = useMemo(() => boardCounts(zoneRows), [zoneRows]);
  const cards = board.counts;

  const visible = useMemo(() => {
    let rows = zoneRows.slice();
    if (show === 'acknowledged') rows = rows.filter((a) => a.status === 'acknowledged');
    else if (show === 'autoCleared') rows = rows.filter((a) => a.status === 'auto_cleared');
    else if (show !== 'all') rows = rows.filter((a) => a.severity === show && a.status === 'active');
    if (sourceFilter !== 'all') rows = rows.filter((a) => a.source === sourceFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (a) =>
          a.subject.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.subjectClass.toLowerCase().includes(q) ||
          a.source.toLowerCase().includes(q),
      );
    }
    rows.sort((a, b) => {
      if (sort === 'count') return b.count - a.count;
      if (sort === 'last-seen') return Date.parse(b.lastSeen) - Date.parse(a.lastSeen);
      const s = SEV_RANK[b.severity] - SEV_RANK[a.severity];
      return s !== 0 ? s : Date.parse(b.lastSeen) - Date.parse(a.lastSeen);
    });
    return rows;
  }, [zoneRows, show, sourceFilter, search, sort]);

  const toggleSelect = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleExpand = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected = visible.length > 0 && visible.every((a) => selected.has(a.alertId));
  const toggleSelectAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((a) => a.alertId)));

  const runSingle = async (alert: Alert, action: 'acknowledge' | 'unacknowledge' | 'clear') => {
    setBusy(true);
    try {
      await actOnAlert(alert, action);
      toast.push({ variant: 'success', title: `Alert ${action === 'unacknowledge' ? 'un-acknowledged' : action + 'd'}` });
      reload();
    } catch (e) {
      toast.push({ variant: 'error', ...toastFromError(e, 'Could not update the alert') });
    } finally {
      setBusy(false);
    }
  };

  const runBulk = async (action: 'acknowledge' | 'clear' | 'dismiss') => {
    const ids = [...selected];
    if (ids.length === 0) return;
    // A4 — bulk Clear on still-active (un-recovered) alerts requires a confirm;
    // Acknowledge / Dismiss are reversible and do not.
    if (action === 'clear') {
      const activeSelected = (alerts ?? []).filter(
        (a) => selected.has(a.alertId) && a.status === 'active',
      ).length;
      if (
        activeSelected > 0 &&
        !window.confirm(
          `Clear ${ids.length} alert${ids.length === 1 ? '' : 's'}? ${activeSelected} ` +
            `${activeSelected === 1 ? 'is' : 'are'} still active — clearing asserts the problem is resolved. ` +
            `If the condition persists it will re-raise on the next evaluation.`,
        )
      ) {
        return;
      }
    }
    setBusy(true);
    try {
      const selectedAlerts = (alerts ?? []).filter((a) => selected.has(a.alertId));
      const { updated, missing } = await bulkActOnAlerts(action, selectedAlerts);
      toast.push({
        variant: 'success',
        title: `${updated.length} alert${updated.length === 1 ? '' : 's'} ${action === 'dismiss' ? 'dismissed' : action + 'd'}`,
        detail: missing.length ? `${missing.length} no longer on the board` : undefined,
      });
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.push({ variant: 'error', ...toastFromError(e, 'Bulk action failed') });
    } finally {
      setBusy(false);
    }
  };

  if (meState.status === 'loading') {
    return (
      <div className="alerts-page">
        <h1>Alerts</h1>
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (!canView) {
    return (
      <div className="alerts-page">
        <h1>Alerts</h1>
        <p className="portal-error" role="alert">
          Alerts are restricted to admin roles.
        </p>
      </div>
    );
  }

  return (
    <div className="alerts-page">
      <h1>Alerts</h1>
      <div className="alerts-crumb">
        Admin → COG → Alerts &nbsp;·&nbsp; operational alerting across all producers (scoring · vendor · deploy · auth)
      </div>

      <div className="alerts-updated" data-testid="alerts-updated" aria-live="polite">
        {board.updatedAt ? (
          <>Updated <InstantTime iso={board.updatedAt} mode="time" /> · refreshes every minute</>
        ) : (
          <span className="alerts-loading"><span className="alerts-spinner" aria-hidden="true" />Loading alerts…</span>
        )}
      </div>
      {zoneWarning && (
        <div className="error-banner" role="alert">
          {zoneWarning}
        </div>
      )}

      {/* Per-zone legend — ALWAYS visible, whatever the filter (Jeff's rule). A platform-wide
          alert is counted once, in its own "All zones" row, never under a zone. */}
      <table className="alerts-zone-legend" data-testid="alerts-zone-legend" aria-label="Alerts by zone">
        <thead>
          <tr><th scope="col">Zone</th><th scope="col">Critical</th><th scope="col">Warning</th><th scope="col">Info</th><th scope="col">Acknowledged</th><th scope="col">Auto-cleared</th><th scope="col">On board</th></tr>
        </thead>
        <tbody>
          {legend.map((r) => (
            <tr key={r.key} data-zone-row={r.key}>
              {/* §6 row 2 — a zone in a table is its country code (All for platform-wide), full name on hover. */}
              <th scope="row">{r.key === PLATFORM_ROW ? <ZoneCell platformWide /> : <ZoneCell zone={r.key} />}</th>
              <td>{r.critical}</td><td>{r.warning}</td><td>{r.info}</td><td>{r.acknowledged}</td><td>{r.autoCleared}</td><td>{r.total}</td>
            </tr>
          ))}
        </tbody>
        {/* Q3 — the Total row. "All zones" above holds only platform-wide alerts; this is the sum,
            and its Critical is the Critical card and the navbar count. */}
        <tfoot>
          <tr data-zone-row="total" className="alerts-zone-legend-total">
            <th scope="row">Total{failedZones.length > 0 ? ' (partial)' : ''}</th>
            <td>{legendSum.critical}</td><td>{legendSum.warning}</td><td>{legendSum.info}</td><td>{legendSum.acknowledged}</td><td>{legendSum.autoCleared}</td><td>{legendSum.total}</td>
          </tr>
        </tfoot>
      </table>

      {/* severity summary cards */}
      <div className="alerts-sumbar">
        {/* Critical card mirrors the navbar (A1, ADR-071 AM1): active + UNACKNOWLEDGED
            criticals only — `boardCounts`, the very number the navbar AlertIndicator shows
            (Q1). Every card counts a row once, by status first: acknowledged and auto-cleared
            rows are counted there, not under their severity. */}
        <SumCard cls="crit" big={cards.critical} label="Critical" sub="unacknowledged → lights navbar" />
        <SumCard cls="warn" big={cards.warning} label="Warning" sub="accrues, no navbar" />
        <SumCard cls="info" big={cards.info} label="Info" sub="accrues, no escalation" />
        <SumCard cls="ok" big={cards.acknowledged} label="Acknowledged" sub="silenced, not cleared" />
      </div>

      {/* Show pills + catalogue search / source / sort (A4, staff-owned A5) */}
      <div className="alerts-filters">
        <span className="alerts-filters-lab">Show:</span>
        {(['all', 'critical', 'warning', 'info', 'acknowledged', 'autoCleared'] as ShowFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            className={`alerts-fpill${show === f ? ' on' : ''}`}
            onClick={() => setShow(f)}
          >
            {SHOW_LABEL[f]} <span className="c">{counts[f]}</span>
          </button>
        ))}
        <div className="alerts-controls">
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="alerts-zone-filter" />
          <input
            className="alerts-search"
            type="search"
            placeholder="Search subject / domain / reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search alerts"
          />
          <select
            className="alerts-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as 'all' | AlertSource)}
            aria-label="Filter by source"
          >
            <option value="all">All sources</option>
            {(Object.keys(SOURCE_LABEL) as AlertSource[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
          <select
            className="alerts-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sort alerts"
          >
            <option value="severity">Sort: severity</option>
            <option value="count">Sort: count</option>
            <option value="last-seen">Sort: last seen</option>
          </select>
        </div>
        <span className="alerts-perm">super_admin acknowledges / clears · nexa_admin views</span>
      </div>

      {/* bulk action bar — appears when rows are selected (A4) */}
      {canAct && selected.size > 0 && (
        <div className="alerts-bulkbar" role="region" aria-label="Bulk actions">
          <span className="alerts-bulkbar-count">{selected.size} selected</span>
          <button type="button" className="btn btn-ack" disabled={busy} onClick={() => runBulk('acknowledge')}>
            Acknowledge
          </button>
          <button type="button" className="btn btn-clear" disabled={busy} onClick={() => runBulk('clear')}>
            Clear
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => runBulk('dismiss')}>
            Dismiss
          </button>
          <button type="button" className="btn-link" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      <div className="alerts-sectionlbl">Active — critical first</div>

      {!alerts ? (
        <div className="alerts-empty">
          <span className="alerts-loading"><span className="alerts-spinner" aria-hidden="true" />Loading alerts from every zone…</span>
        </div>
      ) : visible.length === 0 ? (
        <div className="alerts-empty">
          {counts.all === 0 ? 'No active alerts — the board is clean.' : 'No alerts match the current filter.'}
        </div>
      ) : (
        <div className="alerts-table-wrap" data-testid="alerts-table-wrap">
        <table className="alerts-table" data-testid="alerts-table">
          {/* The mock's widths at the 1314 px table budget. Every column but Alert is fixed; Alert takes the rest
              (548 px at that budget) — so at 1280 px the table narrows by its Alert column, never scrolls sideways. */}
          <colgroup>
            {canAct && <col className="alerts-col-select" />}
            <col className="alerts-col-sev" />
            <col className="alerts-col-alert" />
            <col className="alerts-col-zone" />
            <col className="alerts-col-src" />
            <col className="alerts-col-count" />
            <col className="alerts-col-seen" />
            <col className="alerts-col-status" />
            <col className="alerts-col-actions" />
          </colgroup>
          <thead>
            <tr>
              {canAct && (
                <th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all visible"
                  />
                </th>
              )}
              <th>Severity</th>
              <th>Alert</th>
              <th>Zone</th>
              <th>Source</th>
              <th>Count</th>
              <th>Last seen</th>
              <th>Status</th>
              <th className="alerts-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((a) => (
              <AlertRow
                key={a.alertId}
                alert={a}
                canAct={canAct}
                selected={selected.has(a.alertId)}
                expanded={expanded.has(a.alertId)}
                busy={busy}
                onSelect={() => toggleSelect(a.alertId)}
                onExpand={() => toggleExpand(a.alertId)}
                onAction={runSingle}
              />
            ))}
          </tbody>
        </table>
        </div>
      )}

      <div className="alerts-legend">
        <b>Reading this view.</b> Alerts are <b>grouped — one operational problem is one row with a count</b> (D2),
        sorted <b>critical-first</b>. <b>Acknowledge</b> silences the navbar without resolving (the row dims and stays
        listed); <b>Clear</b> resolves it — and alerts <b>auto-clear</b> when the condition recovers (D3). <b>Info</b>
        accrues here but never lights the navbar (A1). A <b>↻ Flapping</b> alert cleared then re-fired within the window.
        An alert that clears <b>by itself</b> stays listed as <b>Auto-cleared</b> — no longer counted, showing what it was —
        until it is <b>acknowledged</b>; if it fires again it goes back to active.
      </div>
    </div>
  );
}

function SumCard({ cls, big, label, sub }: { cls: string; big: number; label: string; sub: string }) {
  return (
    <div className={`alerts-sumcard ${cls}`}>
      <div className="big">{big}</div>
      <div>
        <div className="lab">{label}</div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

function AlertRow({
  alert,
  canAct,
  selected,
  expanded,
  busy,
  onSelect,
  onExpand,
  onAction,
}: {
  alert: Alert;
  canAct: boolean;
  selected: boolean;
  expanded: boolean;
  busy: boolean;
  onSelect: () => void;
  onExpand: () => void;
  /** ADR-120 — the ALERT, not its id: the transition must go to the zone it fired in. */
  onAction: (alert: Alert, action: 'acknowledge' | 'unacknowledge' | 'clear') => void;
}) {
  const acked = alert.status === 'acknowledged';
  const autoCleared = alert.status === 'auto_cleared';
  const sevLabel = alert.severity[0].toUpperCase() + alert.severity.slice(1);
  const rowCls = `${alert.severity === 'critical' && alert.status === 'active' ? 'row-crit' : ''}${acked ? ' row-ack' : ''}${autoCleared ? ' row-autocleared' : ''}`;
  return (
    <>
      <tr className={rowCls}>
        {canAct && (
          <td>
            <input type="checkbox" checked={selected} onChange={onSelect} aria-label={`Select ${alert.subject}`} />
          </td>
        )}
        <td>
          {autoCleared ? (
            // AM3 — its severity is history now: what it WAS, not what it is.
            <span className={`alerts-sev alerts-sev-was alerts-sev-was-${alert.severity}`}>
              <span className="alerts-sevdot" />
              was {sevLabel}
            </span>
          ) : (
            <span className={`alerts-sev alerts-sev-${alert.severity}`}>
              <span className="alerts-sevdot" />
              {sevLabel}
            </span>
          )}
        </td>
        <td>
          <div className="alerts-subj">{alert.subject}</div>
          {alert.summary && <div className="alerts-subj-sub">{alert.summary}</div>}
        </td>
        <td className="alerts-zone" data-platform-wide={isPlatformWide(alert) ? 'true' : undefined}>
          {isPlatformWide(alert) ? <ZoneCell platformWide /> : <ZoneCell zone={alert.zone ?? HOME_ZONE.code} />}
        </td>
        <td>
          <span className={`alerts-src alerts-src-${alert.source}`}>{alert.source}</span>
        </td>
        <td>
          <span className={`alerts-count${alert.count >= 50 ? ' big' : ''}`}>{alert.count}</span>
        </td>
        <td className="alerts-when">
          <InstantTime iso={alert.lastSeen} mode="day-time" />
          <small className="alerts-when-first">first <InstantTime iso={alert.firstSeen} mode="day-time-plain" /></small>
        </td>
        <td>
          {autoCleared ? (
            <>
              <span className="alerts-stat alerts-stat-auto">✓ Auto-cleared</span>
              {/* Beneath the pill, never inside it — the time must not widen the column. */}
              {alert.clearedAt && (
                <small className="alerts-stat-when"><InstantTime iso={alert.clearedAt} mode="time" /></small>
              )}
            </>
          ) : acked ? (
            <span className="alerts-stat alerts-stat-ack">◌ Acknowledged</span>
          ) : alert.flapping ? (
            <span className="alerts-stat alerts-stat-flap">↻ Flapping</span>
          ) : (
            <span className="alerts-stat alerts-stat-active">● Active</span>
          )}
        </td>
        <td className="alerts-actions-cell">
          {/* The console's icon-button pattern (admin-portal-ui-standards.md §6): coloured by meaning, each with its own
              hover tooltip. An auto-cleared alert has nothing left to resolve, so it offers Acknowledge and Show
              events only. */}
          <ActionsRow>
            {canAct &&
              (acked ? (
                <ActionIcon act="unacknowledge" icon="unacknowledge" label="Un-acknowledge"
                  tip="Un-acknowledge — put it back on the board" disabled={busy}
                  onClick={() => onAction(alert, 'unacknowledge')} />
              ) : (
                <ActionIcon act="acknowledge" icon="acknowledge" label="Acknowledge"
                  tip="Acknowledge — you've seen it; it leaves the board" disabled={busy}
                  onClick={() => onAction(alert, 'acknowledge')} />
              ))}
            {canAct && !autoCleared && (
              <ActionIcon act="clear" icon="clear" label="Clear"
                tip="Clear — the problem is resolved" disabled={busy}
                onClick={() => onAction(alert, 'clear')} />
            )}
            <ActionIcon act="events" icon="events" label={expanded ? 'Hide events' : 'Show events'}
              tip={expanded ? 'Hide the underlying events' : 'Show the underlying events'}
              onClick={onExpand} />
          </ActionsRow>
        </td>
      </tr>
      {expanded && (
        <tr className={rowCls}>
          <td colSpan={canAct ? 9 : 8} style={{ paddingTop: 0 }}>
            <div className="alerts-drill">
              <h4>
                Underlying events — grouped <code>{alert.source}.{alert.type}</code> (subject-class:{' '}
                {alert.subjectClass})
              </h4>
              {(alert.sampleEvents ?? []).length === 0 ? (
                <div className="muted">No sampled events recorded for this alert.</div>
              ) : (
                (alert.sampleEvents ?? []).map((e, i) => (
                  <div className="alerts-evt" key={i}>
                    <span className="t"><InstantTime iso={e.ts} mode="datetime" /></span>
                    <span className="d">{e.detail}</span>
                  </div>
                ))
              )}
              {alert.count > (alert.sampleEvents?.length ?? 0) && (
                <div className="alerts-evt">
                  <span className="t">…</span>
                  <span className="d">
                    {alert.count} total occurrences this window — grouped into this one alert (D2)
                  </span>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
